import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, X, Loader2, ChevronDown, User, Trash2, Check, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import { triggerVibration } from "./WebPushSettings";
import { ReactableMessage, applyReactions } from "./messaging/ReactableMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const consignorAuthHeader = () => {
  const t = localStorage.getItem('consignorToken');
  return t ? { headers: { Authorization: `Bearer ${t}` } } : {};
};

// Polling interval for real-time updates (3 seconds for instant messaging feel)
const POLLING_INTERVAL = 2000; // 2 seconds for instant messenger feel

/**
 * Messaging component for employees and consignors to chat with admin
 * @param {string} userType - "employee" or "consignor"
 * @param {string} userId - User ID (email for consignors, id for employees)
 * @param {string} userName - Display name of the user
 * @param {string} userEmail - Email of the user
 * @param {function} getAuthHeader - Function to get auth header (for employees)
 * @param {boolean} autoExpand - If true, automatically expand the section on mount
 */
export default function MessagingSection({ 
  userType, 
  userId, 
  userName, 
  userEmail,
  getAuthHeader = () => ({}),
  autoExpand = false
}) {
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(autoExpand);
  const inputRef = useRef(null);
  const pollingRef = useRef(null);
  const sectionRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const previousMessageCountRef = useRef(0);
  const isAtBottomRef = useRef(true);

  // Auto-scroll section into view when opened
  useEffect(() => {
    if (autoExpand && sectionRef.current) {
      setTimeout(() => {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 200);
    }
  }, [autoExpand]);
  
  // Check if user is scrolled to bottom of messages
  const checkIfAtBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    const threshold = 50; // pixels from bottom to consider "at bottom"
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  };
  
  // Scroll to bottom only if user was already at bottom
  const scrollToBottomIfNeeded = () => {
    if (isAtBottomRef.current && messagesContainerRef.current) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };
  
  // Track scroll position
  const handleScroll = () => {
    isAtBottomRef.current = checkIfAtBottom();
  };
  
  // Scroll to bottom initially when expanded (don't auto-focus input — let user tap it)
  useEffect(() => {
    if (expanded) {
      // Initial scroll to bottom when opening
      setTimeout(() => {
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
          isAtBottomRef.current = true;
        }
      }, 100);
    }
  }, [expanded]);
  
  // Scroll to bottom when new messages arrive (only if already at bottom)
  useEffect(() => {
    scrollToBottomIfNeeded();
  }, [conversation?.messages?.length]);

  const fetchConversation = async () => {
    try {
      setLoading(true);
      let response;
      
      if (userType === "employee") {
        response = await axios.get(
          `${API}/conversations/employee/my-conversation`,
          getAuthHeader()
        );
      } else {
        response = await axios.get(
          `${API}/conversations/consignor/my-conversation`,
          consignorAuthHeader()
        );
      }
      
      setConversation(response.data);
    } catch (error) {
      console.error("Failed to fetch conversation:", error);
      // Don't show error toast for initial load - conversation might not exist yet
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (userId && expanded) {
      fetchConversation();
      
      // Start polling for new messages when expanded
      pollingRef.current = setInterval(async () => {
        try {
          let response;
          
          if (userType === "employee") {
            response = await axios.get(
              `${API}/conversations/employee/my-conversation`,
              getAuthHeader()
            );
          } else {
            response = await axios.get(
              `${API}/conversations/consignor/my-conversation`,
          consignorAuthHeader()
            );
          }
          
          const newConversation = response.data;
          const newMessages = newConversation?.messages || [];
          const adminMessages = newMessages.filter(m => m.sender_type === "admin");
          
          // Check if there's a new admin message - just vibrate, no toast
          if (adminMessages.length > previousMessageCountRef.current) {
            // Vibrate device if enabled
            triggerVibration([200, 100, 200]);
            
            // Play notification sound
            try {
              const audio = new Audio('/notification.mp3');
              audio.volume = 0.3;
              audio.play().catch(() => {});
            } catch (e) {
              // Audio playback not supported, ignore
            }
          }
          previousMessageCountRef.current = adminMessages.length;
          
          setConversation(newConversation);
        } catch (error) {
          console.error("Polling error:", error);
        }
      }, POLLING_INTERVAL);
    }
    
    // Cleanup polling when collapsed or unmounted
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    };
  }, [userId, userType, expanded]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    try {
      setSending(true);
      
      if (userType === "employee") {
        await axios.post(
          `${API}/conversations/employee/send`,
          { content: newMessage.trim(), sender_name: userName },
          getAuthHeader()
        );
      } else {
        await axios.post(
          `${API}/conversations/consignor/send`,
          { content: newMessage.trim(), sender_name: userName },
          consignorAuthHeader()
        );
      }
      
      setNewMessage("");
      await fetchConversation();
      // No toast - user can see the message appeared
    } catch (error) {
      console.error("Failed to send message:", error);
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatMessageTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  };

  const formatDateSeparator = (isoString) => {
    const date = new Date(isoString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const msgDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (msgDate.getTime() === today.getTime()) {
      return "Today";
    } else if (msgDate.getTime() === yesterday.getTime()) {
      return "Yesterday";
    } else {
      return date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });
    }
  };

  const getDateKey = (isoString) => {
    const date = new Date(isoString);
    return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`;
  };

  // Delete a message (user can only delete their own messages)
  const handleDeleteMessage = async (messageId) => {
    try {
      if (userType === "employee") {
        await axios.delete(
          `${API}/conversations/employee/message/${messageId}`,
          getAuthHeader()
        );
      } else {
        await axios.delete(
          `${API}/conversations/consignor/message/${messageId}`,
          consignorAuthHeader()
        );
      }
      
      toast.success("Message deleted");
      await fetchConversation();
    } catch (error) {
      console.error("Failed to delete message:", error);
      const errorMsg = error.response?.data?.detail || "Failed to delete message";
      toast.error(errorMsg);
    }
  };

  // Format read receipt time
  const formatReadTime = (isoString) => {
    if (!isoString) return null;
    const date = new Date(isoString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString([], { month: "short", day: "numeric" });
  };

  const messages = conversation?.messages || [];
  const unreadCount = messages.filter(m => m.sender_type === "admin" && !m.read).length;

  const handleReact = async (msgId, emoji) => {
    try {
      const res = userType === "employee"
        ? await axios.post(`${API}/conversations/employee/message/${msgId}/react`, { emoji }, getAuthHeader())
        : await axios.post(`${API}/conversations/consignor/message/${msgId}/react`, { emoji }, consignorAuthHeader());
      setConversation((prev) => prev ? { ...prev, messages: applyReactions(prev.messages || [], msgId, res.data.reactions) } : prev);
    } catch (error) {
      toast.error("Couldn't add reaction");
    }
  };

  return (
    <div ref={sectionRef} className="bg-[#1a1a2e]/80 backdrop-blur-sm rounded-2xl border border-white/10 overflow-hidden">
      {/* Header - Always visible */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between p-4 hover:bg-white/5 transition-colors"
        data-testid="messaging-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
            <MessageSquare className="w-5 h-5 text-white" />
          </div>
          <div className="text-left">
            <h3 className="text-white font-medium">Messages</h3>
            <p className="text-white/50 text-sm">
              {messages.length === 0 
                ? "Chat with admin" 
                : `${messages.length} message${messages.length === 1 ? "" : "s"}`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs font-bold px-2 py-1 rounded-full">
              {unreadCount}
            </span>
          )}
          <ChevronDown 
            className={`w-5 h-5 text-white/50 transition-transform ${expanded ? "rotate-180" : ""}`} 
          />
        </div>
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-white/10"
          >
            {/* Messages area */}
            <div 
              ref={messagesContainerRef}
              onScroll={handleScroll}
              className="chat-no-select h-64 overflow-y-auto p-4 space-y-3" 
              data-testid="messages-container"
            >
              {loading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-6 h-6 text-white/50 animate-spin" />
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-12 h-12 text-white/20 mb-2" />
                  <p className="text-white/50 text-sm">No messages yet</p>
                  <p className="text-white/30 text-xs">Send a message to start a conversation</p>
                </div>
              ) : (
                <>
                  {messages.map((msg, index) => {
                    const prevMsg = messages[index - 1];
                    const showDateSeparator = !prevMsg || getDateKey(msg.sent_at) !== getDateKey(prevMsg.sent_at);
                    
                    return (
                      <div key={msg.id}>
                        {showDateSeparator && (
                          <div className="flex items-center gap-3 my-4">
                            <div className="flex-1 h-px bg-white/20"></div>
                            <span className="text-xs font-medium text-white/60 bg-white/10 px-3 py-1 rounded-full">
                              {formatDateSeparator(msg.sent_at)}
                            </span>
                            <div className="flex-1 h-px bg-white/20"></div>
                          </div>
                        )}
                        <ReactableMessage
                          msg={msg}
                          meType={userType}
                          meId={userType === "employee" ? userId : userEmail}
                          align={msg.sender_type === "admin" ? "left" : "right"}
                          isLight={false}
                          onReact={handleReact}
                        >
                        <div className={`flex w-full ${msg.sender_type === "admin" ? "justify-start" : "justify-end"}`}>
                          <div className="group relative">
                            <div
                              className={`max-w-[80%] rounded-2xl px-4 py-2 ${
                                msg.sender_type === "admin"
                                  ? "bg-white/10 text-white rounded-tl-sm"
                                  : "bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-tr-sm"
                              }`}
                            >
                              {msg.sender_type === "admin" && (
                                <p className="text-xs text-white/50 mb-1 flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {msg.sender_name || "Admin"}
                                </p>
                              )}
                              <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                              <div className={`flex items-center gap-1 mt-1 ${
                                msg.sender_type === "admin" ? "text-white/40" : "text-white/70"
                              }`}>
                                <span className="text-xs">{formatMessageTime(msg.sent_at)}</span>
                                {/* Read receipt indicator for user's own messages */}
                                {msg.sender_type !== "admin" && (
                                  <span className="flex items-center ml-2" title={msg.read_at ? `Seen ${formatReadTime(msg.read_at)}` : "Delivered"}>
                                    {msg.read ? (
                                      <>
                                        <CheckCheck className="w-4 h-4 text-blue-300" />
                                        <span className="text-xs ml-1 text-blue-300 font-medium">Read</span>
                                      </>
                                    ) : (
                                      <>
                                        <Check className="w-4 h-4" />
                                        <span className="text-xs ml-1">Sent</span>
                                      </>
                                    )}
                                  </span>
                                )}
                              </div>
                            </div>
                            {/* Delete button for user's own messages */}
                            {msg.sender_type !== "admin" && (
                              <button
                                onClick={() => handleDeleteMessage(msg.id)}
                                className="absolute -left-8 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1.5 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded-full transition-all"
                                title="Delete message"
                                data-testid={`delete-message-${msg.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </button>
                            )}
                          </div>
                        </div>
                        </ReactableMessage>
                      </div>
                    );
                  })}
                </>
              )}
            </div>

            {/* Message input - auto-expanding textarea */}
            <form onSubmit={handleSendMessage} className="p-4 border-t border-white/10">
              <div className="flex items-end gap-2">
                <textarea
                  ref={inputRef}
                  value={newMessage}
                  onChange={(e) => {
                    setNewMessage(e.target.value);
                    // Auto-expand textarea
                    e.target.style.height = 'auto';
                    e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                  }}
                  onFocus={(e) => {
                    // Scroll input into view when keyboard opens on mobile
                    setTimeout(() => {
                      e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
                    }, 300);
                  }}
                  placeholder="Type a message..."
                  rows={1}
                  className="flex-1 bg-white/10 border border-white/20 rounded-2xl px-4 py-2 text-white placeholder-white/40 focus:outline-none focus:border-blue-500/50 resize-none text-base"
                  style={{ minHeight: '40px', maxHeight: '150px', lineHeight: '1.4' }}
                  disabled={sending}
                  data-testid="message-input"
                />
                <Button
                  type="submit"
                  disabled={!newMessage.trim() || sending}
                  className="bg-gradient-to-r from-blue-500 to-purple-600 hover:opacity-90 text-white rounded-full w-10 h-10 p-0 flex-shrink-0"
                  data-testid="send-message-btn"
                >
                  {sending ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Send className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
